import { useInitiateOrgBillingTransferRequestMutation } from "@api/orgBillingTransferRequest.api";
import { OrgBillingTransferCandidates } from "@common/dto/billing/org-billing-transfer-request.dto";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { HStack } from "@design-system/HStack/HStack";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { VStack } from "@design-system/VStack/VStack";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { Form, FormikProvider, useFormik } from "formik";
import { FC, useEffect } from "react";
import { z } from "zod";

export const FIXED_RECIPIENT_TOOLTIP =
    "This client's primary admin isn't part of your organization, so they receive the request.";

const requestBillingTransferFormSchema = z.object({
    newPrimaryAdminUserUuid: z.string().min(1, "Select a billing owner"),
});

type RequestBillingTransferFormValues = z.infer<
    typeof requestBillingTransferFormSchema
>;

const fields = createFormikFields<RequestBillingTransferFormValues>();

interface RequestBillingTransferToClientFormProps {
    clientId: string;
    organizationId: string;
    candidates: OrgBillingTransferCandidates;
    onClose: () => void;
}

export const RequestBillingTransferToClientForm: FC<
    RequestBillingTransferToClientFormProps
> = ({ clientId, organizationId, candidates, onClose }) => {
    const { toast } = useToaster();
    const initiateMutation = useInitiateOrgBillingTransferRequestMutation();
    const { isFixedRecipient } = candidates;
    const firstCandidateUuid = candidates.candidates[0]?.userUuid ?? "";

    const formik = useFormik<RequestBillingTransferFormValues>({
        initialValues: {
            newPrimaryAdminUserUuid: firstCandidateUuid,
        },
        validate: validateFormikWithZodSchema(requestBillingTransferFormSchema),
        onSubmit: submitHelper({
            handler: async ({ newPrimaryAdminUserUuid }) => {
                const recipient = candidates.candidates.find(
                    (candidate) =>
                        candidate.userUuid === newPrimaryAdminUserUuid,
                );
                if (!recipient) {
                    return;
                }

                await initiateMutation.mutateAsync({
                    organizationId,
                    clientId,
                    newPrimaryAdminUserUuid: isFixedRecipient
                        ? undefined
                        : newPrimaryAdminUserUuid,
                });

                toast(`Sent billing transfer request to ${recipient.name}`);
                onClose();
            },
        }),
    });

    const { setFieldValue } = formik;
    const isSelectionEligible = candidates.candidates.some(
        (candidate) =>
            candidate.userUuid === formik.values.newPrimaryAdminUserUuid,
    );

    useEffect(() => {
        if (!isSelectionEligible) {
            void setFieldValue("newPrimaryAdminUserUuid", firstCandidateUuid);
        }
    }, [isSelectionEligible, firstCandidateUuid, setFieldValue]);

    const options = candidates.candidates.map((candidate) => ({
        value: candidate.userUuid,
        label: candidate.name,
        description: candidate.email ?? undefined,
    }));

    return (
        <FormikProvider value={formik}>
            <VStack as={Form} gap={16}>
                <p>
                    Your client will be notified to take over billing for their
                    subscription. You keep paying until they accept.
                </p>

                <fields.Select
                    name="newPrimaryAdminUserUuid"
                    label="Billing owner"
                    options={options}
                    disabled={isFixedRecipient || options.length <= 1}
                    placeholder="Select admin"
                    buttonProps={{
                        size: "xl",
                        tooltip: isFixedRecipient
                            ? FIXED_RECIPIENT_TOOLTIP
                            : undefined,
                    }}
                />

                <HStack as="footer" justify="end" gap={12}>
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={onClose}
                        disabled={formik.isSubmitting}
                        size="xl"
                    >
                        Cancel
                    </Button>
                    <ButtonWithLoader
                        type="submit"
                        variant="primary"
                        disabled={!formik.isValid || formik.isSubmitting}
                        loading={formik.isSubmitting}
                        size="xl"
                    >
                        Send request
                    </ButtonWithLoader>
                </HStack>
            </VStack>
        </FormikProvider>
    );
};
